/* ==========================================================================
   LV- Shop the Feed — Laverne Diamonds
   assets/lv-social.js

   Custom element so the Shopify editor lifecycle is native.

   - pointer drag with mouse and touch, click suppressed only after a real drag
   - videos preload nothing and play only while their tile is on screen
   - arrows disable at the ends; RTL read at runtime
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('lv-social')) return;

  const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DRAG_THRESHOLD = 8;

  class LVSocial extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-social]');
      this.view = this.querySelector('[data-viewport]');
      if (!this.root || !this.view) return;

      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.items = Array.from(this.view.children);
      this.rtl = getComputedStyle(this.root).direction === 'rtl';

      this.bindScroll();
      this.bindDrag();
      this.bindArrows();
      this.bindVideos();
      this.reveal();
      this.update();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._vio) this._vio.disconnect();
      this.view.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this.unbindDrag();
    }

    /* ---- state ------------------------------------------------------------ */
    bindScroll() {
      let ticking = false;
      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this.update(); });
      };
      this.view.addEventListener('scroll', this._onScroll, { passive: true });
      this._onResize = () => this.update();
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    update() {
      const max = this.view.scrollWidth - this.view.clientWidth;
      const pos = this.rtl ? Math.abs(this.view.scrollLeft) : this.view.scrollLeft;
      if (this.prev) this.prev.disabled = pos <= 1;
      if (this.next) this.next.disabled = pos >= max - 1;
      const nav = this.querySelector('[data-nav]');
      if (nav) nav.hidden = max <= 1;
    }

    step() {
      const first = this.items[0];
      const gap = parseFloat(getComputedStyle(this.view).columnGap) || 0;
      return first ? first.getBoundingClientRect().width + gap : this.view.clientWidth * 0.8;
    }

    bindArrows() {
      const go = (dir) => this.view.scrollBy({
        left: this.step() * dir * (this.rtl ? -1 : 1),
        behavior: REDUCED() ? 'auto' : 'smooth'
      });
      if (this.prev) this.prev.addEventListener('click', () => go(-1));
      if (this.next) this.next.addEventListener('click', () => go(1));
    }

    /* ---- drag --------------------------------------------------------------
       Capture is taken only after the threshold is crossed. Capturing on
       pointerdown retargets every event to the viewport, so the browser never
       sees press-and-release on the same link and no click is ever fired.
       ---------------------------------------------------------------------- */
    bindDrag() {
      let armed = false, dragging = false, startX = 0, startScroll = 0, moved = 0, pid = null;

      this._onDown = (e) => {
        if (e.pointerType === 'touch' || e.button !== 0) return;
        armed = true; dragging = false; moved = 0;
        pid = e.pointerId; startX = e.clientX; startScroll = this.view.scrollLeft;
      };

      this._onMove = (e) => {
        if (!armed || e.pointerId !== pid) return;
        const dx = e.clientX - startX;
        moved = Math.abs(dx);
        if (!dragging) {
          if (moved <= DRAG_THRESHOLD) return;
          dragging = true;
          this.view.classList.add('is-dragging');
          try { this.view.setPointerCapture(pid); } catch (err) { /* ignore */ }
        }
        this.view.scrollLeft = startScroll - dx;
      };

      this._onUp = () => {
        if (!armed) return;
        armed = false;
        if (dragging) {
          this.view.classList.remove('is-dragging');
          try { this.view.releasePointerCapture(pid); } catch (err) { /* ignore */ }
          this._suppressClick = true;
        }
        dragging = false; pid = null;
        this.update();
      };

      this._onClickCapture = (e) => {
        if (!this._suppressClick) return;
        this._suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      };

      this.view.addEventListener('click', this._onClickCapture, true);
      this.view.addEventListener('pointerdown', this._onDown);
      this.view.addEventListener('pointermove', this._onMove, { passive: true });
      this.view.addEventListener('pointerup', this._onUp);
      this.view.addEventListener('pointercancel', this._onUp);
      this.view.addEventListener('dragstart', (e) => e.preventDefault());
    }

    unbindDrag() {
      if (!this._onDown) return;
      this.view.removeEventListener('click', this._onClickCapture, true);
      this.view.removeEventListener('pointerdown', this._onDown);
      this.view.removeEventListener('pointermove', this._onMove);
      this.view.removeEventListener('pointerup', this._onUp);
      this.view.removeEventListener('pointercancel', this._onUp);
    }

    /* ---- videos ------------------------------------------------------------
       preload="none" in the markup means nothing downloads until a tile is
       actually scrolled into view. A row of reels would otherwise cost several
       megabytes before the visitor has seen any of them.
       ---------------------------------------------------------------------- */
    bindVideos() {
      const tiles = Array.from(this.root.querySelectorAll('[data-video-tile]'));
      if (!tiles.length || REDUCED() || !('IntersectionObserver' in window)) return;

      this._vio = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const v = en.target.querySelector('video');
          if (!v) return;
          if (en.isIntersecting) {
            v.muted = true;
            v.playsInline = true;
            if (!v._bound) {
              v._bound = true;
              const show = () => en.target.classList.add('is-playing');
              v.addEventListener('canplay', show, { once: true });
              v.addEventListener('playing', show, { once: true });
            }
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.35 });

      tiles.forEach((t) => this._vio.observe(t));
    }

    /* ---- reveal ------------------------------------------------------------ */
    reveal() {
      const items = this.root.querySelectorAll('[data-animate]');
      if (!items.length) return;
      if (!('IntersectionObserver' in window) || REDUCED()) {
        this.root.classList.add('is-in');
        return;
      }
      this.root.classList.add('anim-ready');
      if (this.root.getBoundingClientRect().top < innerHeight * 0.9) {
        requestAnimationFrame(() => this.root.classList.add('is-in'));
        return;
      }
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          this.root.classList.add('is-in');
          this._io.disconnect();
        });
      }, { threshold: 0.12 });
      this._io.observe(this.root);
    }
  }

  customElements.define('lv-social', LVSocial);
})();
